import { Format } from '../Format';
const resolvingStack = [];
const isRecord = (value) => typeof value === 'object' && value !== null;
const isContentNode = (value) => isRecord(value) &&
    typeof value.kind === 'string' &&
    typeof value.typeName === 'string' &&
    'state' in value &&
    'data' in value;
const isOperationDescriptor = (value) => isRecord(value) &&
    typeof value.kind === 'string' &&
    'value' in value &&
    !('typeName' in value);
const isCurrentlyResolving = (node) => resolvingStack.includes(node);
const formattingStateKeys = [
    'emptyFormat',
    'asTextSeparator',
    'listAsTextSeparator',
    'asTextIncludeFieldLabels',
    'textCase',
    'textPrefix',
    'textSuffix',
    'numberUnit',
    'numberFormat',
    'numberPrecision',
    'numberLocale',
    'numberRoundingMode',
    'numberCurrency',
    'dateFormat',
    'dateGranularity',
    'dateIncludeTime',
    'dateRelativeUnit',
    'primaryValueLocator',
    'disabled',
    'displayDisabled',
];
export const resolvePrimaryValueContentNode = (node) => {
    const locator = node.state?.primaryValueLocator;
    if (!locator?.length)
        return node;
    const represented = node.kind === 'FIELD' || node.kind === 'INPUT_VALUE'
        ? node.ofType
        : node;
    if (!represented)
        return node;
    const found = findByPrimaryLocator(locator)(represented);
    return found ?? node;
};
const matcherMatches = (node, matcher) => {
    const [matcherType, matcherValue] = matcher;
    if (matcherType === 'key') {
        return 'key' in node && node.key === matcherValue;
    }
    if (matcherType === 'typeName') {
        return node.typeName === matcherValue;
    }
    if (matcherType === 'kind') {
        return node.kind === matcherValue;
    }
    if (matcherType === 'index') {
        return false;
    }
    return false;
};
const findByPrimaryLocator = (locator) => (node) => {
    if (!locator.length)
        return node;
    const [current, ...rest] = locator;
    if (matcherMatches(node, current)) {
        if (rest.length === 0)
            return node;
        return searchChildrenForPrimary(rest)(node);
    }
    return searchChildrenForPrimary(locator)(node);
};
const searchChildrenForPrimary = (locator) => (node) => {
    switch (node.kind) {
        case 'OBJECT':
        case 'INPUT_OBJECT': {
            for (const field of node.fields ?? []) {
                const found = findByPrimaryLocator(locator)(field);
                if (found)
                    return found;
            }
            return null;
        }
        case 'FIELD':
        case 'INPUT_VALUE': {
            const ofType = node.ofType;
            return ofType ? findByPrimaryLocator(locator)(ofType) : null;
        }
        case 'LIST': {
            const child = node.items?.[0] ??
                node.ofType;
            return child ? findByPrimaryLocator(locator)(child) : null;
        }
        default:
            return null;
    }
};
export const mergeFormattingState = (defaults, nodeState, overrides) => {
    const merged = { ...defaults };
    const apply = (source) => {
        if (!source)
            return;
        for (const key of formattingStateKeys) {
            if (!Object.prototype.hasOwnProperty.call(source, key))
                continue;
            const value = source[key];
            if (value !== undefined) {
                ;
                merged[key] = value;
            }
        }
    };
    apply(nodeState);
    apply(overrides);
    if (merged.asTextSeparator === undefined &&
        (merged.listAsTextSeparator === 'pipe' ||
            merged.listAsTextSeparator === 'comma')) {
        merged.asTextSeparator = merged.listAsTextSeparator;
    }
    return merged;
};
const getRepresentedContentOverrides = (nodeState) => {
    const overrides = mergeFormattingState({}, nodeState);
    delete overrides.primaryValueLocator;
    return overrides;
};
const getCompositeChildOverrides = (state) => ({
    emptyFormat: state.emptyFormat,
    textCase: state.textCase,
    numberUnit: state.numberUnit,
    numberFormat: state.numberFormat,
    numberPrecision: state.numberPrecision,
    numberLocale: state.numberLocale,
    numberRoundingMode: state.numberRoundingMode,
    numberCurrency: state.numberCurrency,
    dateFormat: state.dateFormat,
    dateGranularity: state.dateGranularity,
    dateIncludeTime: state.dateIncludeTime,
    dateRelativeUnit: state.dateRelativeUnit,
});
const getFinalCustomAsText = (state) => {
    if (!state || !Object.prototype.hasOwnProperty.call(state, 'asText')) {
        return { isFinal: false };
    }
    const configured = state.asText;
    if (configured === undefined || isOperationDescriptor(configured)) {
        return { isFinal: false };
    }
    return { isFinal: true, value: configured };
};
const applyDecorationsOnly = (body, state) => {
    if (body === '' ||
        (body === '-' && (state.emptyFormat ?? 'hyphen') === 'hyphen')) {
        return body;
    }
    const cased = state.textCase === 'all'
        ? body.toUpperCase()
        : state.textCase === 'word'
            ? body.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1))
            : body;
    let result = cased;
    if (typeof state.textPrefix === 'string' && state.textPrefix.length > 0) {
        result = `${state.textPrefix}${result}`;
    }
    if (typeof state.textSuffix === 'string' && state.textSuffix.length > 0) {
        result = `${result}${state.textSuffix}`;
    }
    return result;
};
const absentResult = (state, data) => ({
    text: Format.empty(state)(data),
    isAbsent: true,
});
const presentResult = (text) => ({
    text,
    isAbsent: false,
});
const resolveCustomAsText = (configured) => {
    if (isContentNode(configured)) {
        return representInner()(configured);
    }
    if (Array.isArray(configured) &&
        configured.length === 1 &&
        isContentNode(configured[0])) {
        return representInner()(configured[0]);
    }
    if (typeof configured === 'string' ||
        typeof configured === 'number' ||
        typeof configured === 'boolean') {
        return presentResult(String(configured));
    }
    if (configured === null)
        return presentResult('');
    try {
        return presentResult(JSON.stringify(configured) ?? '');
    }
    catch {
        return presentResult(String(configured));
    }
};
const formatScalarBody = (state, typeName, data) => {
    if (typeName === 'Boolean')
        return Format.boolean(state)(data);
    if (typeName === 'Int')
        return Format.integer(state)(data);
    if (typeName === 'Float')
        return Format.float(state)(data);
    if (typeName === 'Date' || typeName === 'DateTime') {
        if (state.dateRelativeUnit) {
            return Format.relativeDate(state)(data);
        }
        return Format.date(state)(data);
    }
    if (typeName === 'ID')
        return Format.id(state)(data);
    if (typeName === 'String')
        return Format.text(state)(data);
    return Format.format(state)(data);
};
const isEmptyScalarData = (data) => data === null || data === undefined || data === '';
const isWhollyEmptyData = (data) => {
    if (isEmptyScalarData(data))
        return true;
    if (Array.isArray(data))
        return data.every(isWhollyEmptyData);
    if (isRecord(data) &&
        (Object.getPrototypeOf(data) === Object.prototype ||
            Object.getPrototypeOf(data) === null)) {
        return Object.values(data).every(isWhollyEmptyData);
    }
    return false;
};
const fieldLabel = (field) => {
    const label = field.state?.label;
    if (typeof label === 'string' && label.length > 0)
        return label;
    if ('key' in field && typeof field.key === 'string')
        return field.key;
    return field.typeName;
};
const representInner = (overrides, ignoreCustom = false) => (node) => {
    if (!node) {
        return absentResult(overrides ?? {}, null);
    }
    const nodeState = (node.state ?? {});
    const state = mergeFormattingState({}, nodeState, overrides);
    const representedContentOverrides = getRepresentedContentOverrides(nodeState);
    if (isCurrentlyResolving(node)) {
        return intrinsicRepresent(node, state, true, representedContentOverrides);
    }
    resolvingStack.push(node);
    try {
        if (!ignoreCustom) {
            const asTextConfigured = getFinalCustomAsText(nodeState);
            if (asTextConfigured.isFinal) {
                const resolved = resolveCustomAsText(asTextConfigured.value);
                if (resolved.text === Format.empty({ emptyFormat: 'hyphen' })(null) &&
                    isWhollyEmptyData(node.data)) {
                    return intrinsicRepresent(node, state, true, representedContentOverrides);
                }
                return resolved;
            }
        }
        return intrinsicRepresent(node, state, false, representedContentOverrides);
    }
    finally {
        resolvingStack.pop();
    }
};
const intrinsicRepresent = (node, state, ignoreOfTypeCustom = false, representedContentOverrides = {}) => {
    switch (node.kind) {
        case 'FIELD':
        case 'INPUT_VALUE': {
            const primary = resolvePrimaryValueContentNode(node);
            if (primary !== node) {
                return representInner(representedContentOverrides, ignoreOfTypeCustom)(primary);
            }
            const ofType = node.ofType;
            if (!ofType)
                return absentResult(state, null);
            return representInner(representedContentOverrides, ignoreOfTypeCustom)(ofType);
        }
        case 'LIST': {
            const items = node.items ?? [];
            if (items.length === 0)
                return absentResult(state, null);
            const childOverrides = getCompositeChildOverrides(state);
            const parts = items.map(item => representInner(childOverrides)(item).text);
            const joined = Format.join({
                asTextSeparator: state.asTextSeparator ?? 'pipe',
            })(parts);
            return presentResult(applyDecorationsOnly(joined, state));
        }
        case 'OBJECT':
        case 'INPUT_OBJECT': {
            const primary = resolvePrimaryValueContentNode(node);
            if (primary !== node) {
                return representInner(getCompositeChildOverrides(state))(primary);
            }
            const fields = (node.fields ?? []);
            const lines = [];
            const parentIncludeLabels = state.asTextIncludeFieldLabels;
            const childOverrides = getCompositeChildOverrides(state);
            for (const field of fields) {
                const fieldState = (field.state ?? {});
                if (fieldState.disabled === true ||
                    fieldState.displayDisabled === true) {
                    continue;
                }
                const customFinal = getFinalCustomAsText(fieldState).isFinal;
                const valueText = representInner(childOverrides)(field).text;
                let includeLabel;
                if (customFinal) {
                    includeLabel = fieldState.asTextIncludeFieldLabels === true;
                }
                else {
                    includeLabel = parentIncludeLabels !== false;
                }
                lines.push(includeLabel ? `${fieldLabel(field)}: ${valueText}` : valueText);
            }
            if (lines.length === 0)
                return absentResult(state, null);
            const joined = Format.join({
                asTextSeparator: state.asTextSeparator ?? 'newline',
            })(lines);
            return presentResult(applyDecorationsOnly(joined, state));
        }
        case 'ENUM': {
            if (node.contentNode) {
                return representInner(getCompositeChildOverrides(state))(node.contentNode);
            }
            if (typeof node.data === 'string' ||
                typeof node.data === 'number' ||
                typeof node.data === 'boolean') {
                return presentResult(applyDecorationsOnly(String(node.data), state));
            }
            return absentResult(state, node.data);
        }
        case 'ENUM_VALUE': {
            if (typeof node.data === 'string' ||
                typeof node.data === 'number' ||
                typeof node.data === 'boolean') {
                return presentResult(applyDecorationsOnly(String(node.data), state));
            }
            return absentResult(state, node.data);
        }
        case 'UNION': {
            const selected = node.contentNode;
            if (!selected)
                return absentResult(state, null);
            return representInner(getCompositeChildOverrides(state))(selected);
        }
        case 'SCALAR': {
            const body = formatScalarBody(state, node.typeName, node.data);
            if (isEmptyScalarData(node.data)) {
                return { text: body, isAbsent: true };
            }
            if (node.typeName === 'String' ||
                node.typeName === 'ID' ||
                (node.typeName !== 'Boolean' &&
                    node.typeName !== 'Int' &&
                    node.typeName !== 'Float' &&
                    node.typeName !== 'Date' &&
                    node.typeName !== 'DateTime')) {
                return presentResult(body);
            }
            return presentResult(applyDecorationsOnly(body, state));
        }
        default: {
            if ('data' in node) {
                const data = node.data;
                const text = Format.format(state)(data);
                if (isEmptyScalarData(data)) {
                    return { text, isAbsent: true };
                }
                return presentResult(text);
            }
            return absentResult(state, null);
        }
    }
};
const asTextInner = (overrides) => (node) => representInner(overrides)(node).text;
const asIntrinsicText = (node) => intrinsicRepresent(node, mergeFormattingState({}, node.state ?? {}), true)
    .text;
export const ContentNodeText = {
    asText: asTextInner,
    asIntrinsicText,
    mergeFormattingState,
    resolvePrimaryValueContentNode,
};
//# sourceMappingURL=ContentNodeText.js.map